import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Icon, Label, Popup } from 'semantic-ui-react';

const INLINE_LIMIT = 5;
const INLINE_LOCATIONS = 2;
// Placeholder the integration API gives captureAllStories campaigns created without hashtags.
const CAPTURE_ALL_STORIES_HASHTAG = '#captureallstories';
const CAPTURE_ALL_STORIES_TEXT =
  'All stories will be captured for this campaign. This will create a lot of data, which will require processing.';

// Spacing lives on the chip so it also applies inside popups, which render outside the header.
const chipStyle = {
  border: 'none',
  fontWeight: 600,
  margin: '2px 6px 2px 0',
  maxWidth: 240,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};
const hashtagStyle = { ...chipStyle, background: '#f1f2f3', color: 'rgba(0,0,0,.7)' };
const mentionStyle = { ...chipStyle, background: '#fdeef4', color: '#b1306e' };
const moreStyle = { ...chipStyle, background: 'transparent', color: 'rgba(0,0,0,.55)', cursor: 'pointer' };
const popupStyle = { maxWidth: 520, maxHeight: 320, overflowY: 'auto' };
const locationStyle = { ...chipStyle, background: '#e8f1fb', color: '#1e5b99' };
const moreLocationsStyle = { ...locationStyle, cursor: 'pointer' };
const locationIconStyle = { marginRight: 4 };
const popupLocationStyle = { display: 'block', marginBottom: 6 };
// Tall enough for the maximum of 10 locations without scrolling.
const locationsPopupStyle = { ...popupStyle, maxHeight: 400 };

const locationUrl = ({ id }) => `https://www.instagram.com/explore/locations/${id}/`;

const isMention = tag => _.startsWith(_.trim(tag), '@');

const renderTag = (ht = '', i) => (isMention(ht) ? (
  <a
    key={`${ht}-${i}`}
    href={`https://www.instagram.com/${_.trim(ht).substring(1)}`}
    target="_blank"
    rel="noopener noreferrer"
    title={`Open ${_.trim(ht)} on Instagram`}
  >
    <Label style={mentionStyle}>{ht}</Label>
  </a>
) : (
  <Label key={`${ht}-${i}`} style={hashtagStyle} title={ht}>{ht}</Label>
));

const renderLocation = (location, style) => (
  <a
    key={location.id}
    href={locationUrl(location)}
    target="_blank"
    rel="noopener noreferrer"
    title={`${location.name} · Open on Instagram`}
    style={style}
  >
    <Label style={locationStyle}>
      <Icon name="marker" style={locationIconStyle} />
      {location.name}
    </Label>
  </a>
);

const CaptureAllStoriesLabel = () => (
  <Popup
    trigger={<Label style={hashtagStyle}><Icon name="lightning" />ALL STORIES</Label>}
    content={CAPTURE_ALL_STORIES_TEXT}
  />
);

const CampaignHashtagsHeader = ({ hashtags, locations }) => {
  const isCaptureAllOnly = _.size(hashtags) === 1
    && _.toLower(_.trim(_.head(hashtags))) === CAPTURE_ALL_STORIES_HASHTAG;
  const tags = isCaptureAllOnly ? [] : _.sortBy(hashtags, tag => (isMention(tag) ? 1 : 0));
  const places = _.filter(locations, 'id');
  if (_.isEmpty(tags) && _.isEmpty(places) && !isCaptureAllOnly) return null;

  const visible = _.take(tags, INLINE_LIMIT);
  const hidden = _.slice(tags, INLINE_LIMIT);
  const hiddenPlaces = _.slice(places, INLINE_LOCATIONS);
  return (
    <span className="campaign-hashtags-header">
      { isCaptureAllOnly && <CaptureAllStoriesLabel /> }
      { _.map(visible, renderTag) }
      {
        !_.isEmpty(hidden) &&
        <Popup
          on="click"
          wide="very"
          position="bottom left"
          trigger={<Label style={moreStyle}>{`+${_.size(hidden)} more`}</Label>}
          content={<div style={popupStyle}>{ _.map(hidden, renderTag) }</div>}
        />
      }
      { _.map(_.take(places, INLINE_LOCATIONS), location => renderLocation(location)) }
      {
        !_.isEmpty(hiddenPlaces) &&
        <Popup
          on="click"
          wide="very"
          position="bottom left"
          trigger={
            <Label style={moreLocationsStyle}>
              {`+${_.size(hiddenPlaces)} ${_.size(hiddenPlaces) === 1 ? 'location' : 'locations'}`}
            </Label>
          }
          content={
            <div style={locationsPopupStyle}>
              { _.map(places, location => renderLocation(location, popupLocationStyle)) }
            </div>
          }
        />
      }
    </span>
  );
};

CampaignHashtagsHeader.propTypes = {
  hashtags: PropTypes.array,
  locations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  })),
};

CampaignHashtagsHeader.defaultProps = {
  hashtags: [],
  locations: [],
};

export default CampaignHashtagsHeader;



/** WEBPACK FOOTER **
 ** ./src/components/campaign-hashtags-header/index.js
 **/